import React, { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PopoverBody, UncontrolledPopover } from 'reactstrap';
import dashboardService from '../../../services/dashboardService';
import {
  DashboardDocumentInfo,
  DashboardHighlightTooltip,
  HighlightDataRow,
  QueryUser,
} from '../../../services/types/Dashboard';
import { Highlight } from '../../../services/types/Operation';
import { ButtonBar, LinkButton, Section, SpinnerArea } from '../../../shared/components';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import { customDateStr, findMapId, getDocLinkName } from '../../../shared/misc/utils';
import Role from '../../../types/role';
import MainContext from '../../App/MainProvider/context';
import HighlightComp from '../../Operations/Highlight';
import reducer from './reducer';

interface Props {
  scrollToGantt: () => void;
}

const OverviewPanel: React.FC<Props> = ({ scrollToGantt }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, inSuperUserMode } = useContext(MainContext);

  const canSeeGrievance =
    inSuperUserMode() ||
    user.roleCode === Role.HSO ||
    user.secondRoleCode === Role.HSO ||
    user.grievanceRole === 'GPC' ||
    user.grievanceRole === 'GPT' ||
    user.isGrievanceGH;

  const canSeeClosure =
    user.roleCode === Role.HSO || user.secondRoleCode === Role.HSO || inSuperUserMode();

  const roleBar =
    user.roleCode === Role.GroupHead ||
    user.secondRoleCode === Role.GroupHead ||
    user.roleCode === Role.HSO ||
    user.secondRoleCode === Role.HSO ||
    user.roleCode === Role.Specialist ||
    user.secondRoleCode === Role.Specialist;

  const showBar = roleBar || canSeeGrievance;

  const queryUsers = [
    { id: 1, text: t('miOverview'), htmlId: 'tabMy', mapped: QueryUser.My },
    { id: 2, text: t('myGroupsOverview'), htmlId: 'tabGroup', mapped: QueryUser.MyGroup },
    { id: 3, text: t('generalOverview'), htmlId: 'tabAll', mapped: QueryUser.All },
    ...(canSeeGrievance
      ? [{ id: 4, text: t('gpOverview'), htmlId: 'tabGrievance', mapped: QueryUser.Grievance }]
      : []),
  ].filter((x) => {
    // 3. Si es el nuevo tab, solo lo mostramos si tiene el permiso
    if (x.mapped === QueryUser.Grievance) {
      return canSeeGrievance;
    }

    if (user.roleCode === Role.GroupHead || user.secondRoleCode === Role.GroupHead) {
      return true;
    }
    if (
      user.roleCode === Role.HSO ||
      user.secondRoleCode === Role.HSO ||
      user.roleCode === Role.Specialist ||
      user.secondRoleCode === Role.Specialist
    ) {
      return x.mapped !== QueryUser.MyGroup;
    }

    return x.mapped === QueryUser.All;
  });

  const [data, loading, filter, dispatch] = useServiceReducer(
    dashboardService.getOverviewData,
    reducer,
    { queryUser: queryUsers.length > 0 ? queryUsers[0].mapped : QueryUser.All }
  );

  const onFilterChange = (value: number) => {
    const queryItem = queryUsers.find((x) => x.id === value);
    if (queryItem) {
      dispatch({ type: 'QUERY_USER', value: queryItem.mapped });
    }
  };

  const getOpOverviewLink = (opSummaryFilter?: string) => {
    switch (opSummaryFilter) {
      case 'ALL':
        return `/operations?group=ALL&assigned=${filter.queryUser !== 'ALL'}&type=assigned`;
      case 'PREP':
        return `/operations?group=PREP&assigned=${filter.queryUser !== 'ALL'}&type=assigned`;
      case 'EXEC':
        return `/operations?group=EXEC&assigned=${filter.queryUser !== 'ALL'}&type=assigned`;
      default:
        return undefined;
    }
  };

  const renderHighlightData = (row: HighlightDataRow[], nameDate?: string) =>
    row.map((x) => (
      <div key={x.name} className="d-flex flex-column">
        <div className="d-flex flex-row">
          {/* <div style={{ width: '90px' }}> */}
          <div className="text-nowrap">
            <LinkButton
              onClick={() => {
                navigate(`/operations/${x.name}`);
              }}
              text={x.name}
            />
          </div>
          <div className="text-nowrap">
            {nameDate || 'Date'}: <b>{`${customDateStr(x.date) || `(${t('noDataInCompass')})`}`}</b>
          </div>
        </div>
      </div>
    ));

  const renderHighlightDataOV = (row: HighlightDataRow[], lastSPR?: boolean) =>
    row.map((x) => (
      <div key={x.name} className="d-flex flex-column">
        <div className="d-flex flex-row">
          <div className="text-nowrap">
            <LinkButton
              onClick={() => {
                navigate(`/operations/${x.name}`);
              }}
              text={x.name}
            />
          </div>
          {lastSPR && (
            <div className="text-nowrap">
              {'Last SPR: '}
              <b>{`${customDateStr(x.date) || `(${t('noDataInCompass')})`}`}</b>
            </div>
          )}
        </div>
      </div>
    ));

  const renderHighlightGrievanceDataOV = (row: HighlightDataRow[]) =>
    row.map((x) => (
      <div key={x.name} className="d-flex flex-column">
        <div className="d-flex flex-row">
          <div className="text-nowrap">
            <LinkButton
              onClick={() => {
                navigate(`/grievance-protocol/details/${x.name}`);
              }}
              text={x.name}
            />
          </div>
        </div>
      </div>
    ));

  const renderOpSummaryHighlights = (highlights: DashboardHighlightTooltip[]) =>
    highlights.map((x) => {
      const hasTooltip = true;
      return (
        <Fragment key={x.name}>
          <div
            id={`operation-summary-${x.name}`}
            style={{ cursor: hasTooltip ? 'pointer' : undefined }}
          >
            <HighlightComp
              name={
                x.name === 'ALL'
                  ? `operationSummaryTypes.${x.name}.${filter.queryUser}`
                  : `operationSummaryTypes.${x.name}`
              }
              value={x.value}
              color={x.color}
              link={!hasTooltip ? getOpOverviewLink(x.name) : undefined}
            />
          </div>
          {hasTooltip && (
            <UncontrolledPopover
              placement="bottom"
              target={`operation-summary-${x.name}`}
              trigger="legacy"
              className="popover-gantt"
            >
              <PopoverBody>
                <h5 className="mb-2">
                  {t(
                    `operationSummaryTypes.${x.name}${
                      x.name === 'ALL' ? `.${filter.queryUser}` : ''
                    }`
                  )}
                </h5>
                {x.name === 'SUP'
                  ? renderHighlightDataOV(x.data, true)
                  : renderHighlightDataOV(x.data)}
              </PopoverBody>
            </UncontrolledPopover>
          )}
        </Fragment>
      );
    });

  const renderGPSummaryHighlights = (highlights: DashboardHighlightTooltip[]) =>
    highlights.map((x) => {
      const hasTooltip = true;
      return (
        <Fragment key={x.name}>
          <div
            id={`operation-summary-${x.name}`}
            style={{ cursor: hasTooltip ? 'pointer' : undefined }}
          >
            <HighlightComp
              name={
                x.name === 'ALL'
                  ? `grievanceSummaryTypes.${x.name}.${filter.queryUser}`
                  : `grievanceSummaryTypes.${x.name}`
              }
              value={x.value}
              color={x.color}
              link={!hasTooltip ? getOpOverviewLink(x.name) : undefined}
            />
          </div>
          {hasTooltip && (
            <UncontrolledPopover
              placement="bottom"
              target={`operation-summary-${x.name}`}
              trigger="legacy"
              className="popover-gantt"
            >
              <PopoverBody>
                <h5 className="mb-2">
                  {t(
                    `grievanceSummaryTypes.${x.name}${
                      x.name === 'ALL' ? `.${filter.queryUser}` : ''
                    }`
                  )}
                </h5>
                {renderHighlightGrievanceDataOV(x.data)}
              </PopoverBody>
            </UncontrolledPopover>
          )}
        </Fragment>
      );
    });

  const renderMilestoneSummaryHighlights = (highlights: DashboardHighlightTooltip[]) =>
    highlights.map((x) => (
      <Fragment key={x.name}>
        <div id={`milestone-summary-${x.name}`} style={{ cursor: 'pointer' }}>
          <HighlightComp name={`milestoneSummaryTypes.${x.name}`} value={x.value} color={x.color} />
        </div>
        {x.value > 0 && (
          <UncontrolledPopover
            placement="bottom"
            target={`milestone-summary-${x.name}`}
            trigger="legacy"
            className="popover-gantt"
          >
            <PopoverBody>
              <h5 className="mb-2">{t(`milestoneSummaryTypes.${x.name}`)}</h5>
              {renderHighlightData(x.data)}
            </PopoverBody>
          </UncontrolledPopover>
        )}
      </Fragment>
    ));

  const renderTasksSummaryHighlights = (highlights: Highlight[]) =>
    highlights.map((x) => (
      <Fragment key={x.name}>
        <HighlightComp
          name={`taskSummaryTypes.${x.name}`}
          value={x.value}
          color={x.color}
          link={`?tasks=${x.name}`}
          actionOnLinkClick={scrollToGantt}
        />
      </Fragment>
    ));

  const renderLastDocuments = (docs: DashboardDocumentInfo[]) =>
    docs.map((doc) => {
      const docType = getDocLinkName(doc.documentType);
      return (
        <Fragment key={doc.linkName}>
          <LinkButton
            onClick={() => {
              navigate(`/operations/${doc.operationNumber}/${docType}`);
            }}
            text={doc.linkName}
          />
        </Fragment>
      );
    });

  const renderLastDocumentsGP = (docs: DashboardDocumentInfo[]) =>
    docs.map((doc) => (
      <div key={doc.linkName} className="d-flex flex-column">
        <div className="d-flex flex-row">
          <div className="text-nowrap">
            <Fragment key={doc.linkName}>
              <LinkButton
                underline
                onClick={() => {
                  navigate(`/grievance-protocol/details/${doc.operationNumber}`);
                }}
                text={doc.operationNumber}
              />
            </Fragment>
          </div>
          <div className="text-truncate-custom">{doc.linkName}</div>
        </div>
      </div>
    ));

  return (
    <Section title={t('overview')}>
      <SpinnerArea loading={loading}>
        {showBar && (
          <div className="button-container-bar">
            <div className="scrollablex-wrapper">
              <ButtonBar
                id="queryUserBar"
                value={findMapId(queryUsers, filter.queryUser)}
                onChange={onFilterChange}
                elements={queryUsers}
              />
            </div>
          </div>
        )}

        {data && data.grievanceSummary && filter.queryUser === QueryUser.Grievance ? (
          <div className="dashboard-overview">
            <div className="dashboard-overview__section">
              <h5 className="mb-2">{t('grievanceSummary')}:</h5>
              {data && data.grievanceSummary && renderGPSummaryHighlights(data.grievanceSummary)}
            </div>
            <div className="dashboard-overview__section is-double">
              <h5 className="mb-2">{t('documentsPendingApproval')}:</h5>
              {data && data.grievanceDocuments && renderLastDocumentsGP(data.grievanceDocuments)}
            </div>
            {canSeeClosure && (
              <div className="dashboard-overview__section">
                <h5 className="mb-2">{t('closureAuthorizationPending')}:</h5>
                {data && data.grievanceClosure && renderLastDocumentsGP(data.grievanceClosure)}
              </div>
            )}
          </div>
        ) : (
          <div className="dashboard-overview">
            <div className="dashboard-overview__section">
              <h5 className="mb-2">{t('operationSummary')}:</h5>
              {data && data.operationSummary && renderOpSummaryHighlights(data.operationSummary)}
            </div>
            <div className="dashboard-overview__section">
              <h5 className="mb-2">{t('upcomingMilestones')}:</h5>
              {data && data.upcomingMilestones && (
                <div className="d-flex">
                  <div className="d-flex flex-column">
                    {renderMilestoneSummaryHighlights(
                      data.upcomingMilestones.filter((_x, i) => i < 5)
                    )}
                  </div>
                  {data.upcomingMilestones.length > 5 && (
                    <div className="d-flex flex-column">
                      {renderMilestoneSummaryHighlights(
                        data.upcomingMilestones.filter((_x, i) => i >= 5)
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="dashboard-overview__section">
              <h5 className="mb-2">{t('pendingTasks')}:</h5>
              {data && data.pendingTasks && renderTasksSummaryHighlights(data.pendingTasks)}
            </div>
            <div className="dashboard-overview__section">
              <h5 className="mb-2">{t('dashboardLastDocuments')}:</h5>
              {data && data.lastDocuments && renderLastDocuments(data.lastDocuments)}
            </div>
          </div>
        )}
      </SpinnerArea>
    </Section>
  );
};

export default OverviewPanel;
